import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import TaskTable from "../components/TaskTable";
import TaskDrawer from "../components/TaskDrawer";
import { useData } from "../lib/DataContext";
import { Button } from "../components/ui/button";
import { Star, FolderKanban, CheckSquare } from "lucide-react";

export default function MyFavourites() {
  const { favourites, toggleFavourite } = useData();
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    const [t, p] = await Promise.all([
      api.get("/tasks", { params: { favourite: true, page_size: 200 } }),
      api.get("/projects", { params: { favourite: true } }),
    ]);
    setTasks(t.data.items);
    setProjects(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const unfavTask = async (id) => { await toggleFavourite("task", id); load(); };
  const unfavProject = async (id) => { await toggleFavourite("project", id); load(); };

  return (
    <div className="p-6" data-testid="my-favourites-page">
      <PageHeader title="My Favourites" subtitle={`${tasks.length} tasks · ${projects.length} projects`} />

      <div className="flex items-center gap-2 mb-3 mt-1">
        <FolderKanban size={16} className="text-violet-600" />
        <h2 className="text-base font-semibold text-slate-800">Favourite Projects</h2>
      </div>
      {projects.length === 0 ? (
        <div className="text-sm text-slate-400 mb-8" data-testid="fav-projects-empty">No favourite projects yet. Tap the star on any project to add it here.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
          {projects.map((p) => (
            <div key={p.id} data-testid={`fav-project-card-${p.human_id}`} className="bg-white border rounded-xl p-5">
              <div className="flex items-center gap-2">
                <button data-testid={`fav-project-unfav-${p.human_id}`} onClick={() => unfavProject(p.id)} title="Remove favourite" className="text-amber-500 shrink-0">
                  <Star size={17} className="fill-amber-500" />
                </button>
                <div className="h-9 w-9 rounded-lg bg-violet-100 flex items-center justify-center"><FolderKanban size={18} className="text-violet-600" /></div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{p.name}</div>
                  <div className="text-xs text-slate-400 font-mono">{p.code} · {p.human_id}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 text-center">
                {[["Total", p.stats.total_tasks], ["Open", p.stats.open], ["Overdue", p.stats.overdue], ["Blocked", p.stats.blocked]].map(([l, v]) => (
                  <div key={l}><div className="text-lg font-bold font-mono text-slate-800">{v}</div><div className="text-[10px] text-slate-400">{l}</div></div>
                ))}
              </div>
              <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => nav(`/tasks?project_id=${p.id}`)}>View Tasks</Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <CheckSquare size={16} className="text-blue-600" />
        <h2 className="text-base font-semibold text-slate-800">Favourite Tasks</h2>
      </div>
      {tasks.length === 0 ? (
        <div className="text-sm text-slate-400" data-testid="fav-tasks-empty">No favourite tasks yet. Tap the star on any task to add it here.</div>
      ) : (
        <TaskTable tasks={tasks} onOpen={setOpenId} favourites={favourites.task} onToggleFav={unfavTask} />
      )}

      {openId && <TaskDrawer taskId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
